import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  useColorMode,
  useToken,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetJobQuery,
  useSeparateStemsMutation,
} from '../redux/services/mixgenie';
import { useAuth } from '../auth/AuthProvider';

/**
 * Studio v1 — a light-touch DAW mixer.
 *
 * Given a completed song (jobId), the composer FE posts `/generation-jobs/:id/stems`
 * which kicks off Demucs (via PromptLab/Replicate) on the server. We poll
 * the job every 3s until `stems.status === 'ready'`, then decode the 4
 * stem URLs into a single AudioContext, wire them through per-stem
 * GainNodes, and render a mixer with:
 *   - inline canvas waveforms per stem (peaks pre-computed once at decode)
 *   - vertical volume sliders + Mute + Solo per channel
 *   - a master transport (play / pause / scrub) at the bottom
 *
 * Web Audio API design notes:
 *   - BufferSourceNodes are one-shot — every play/seek creates a fresh set.
 *   - `startedAtCtxTime` + `pausedOffset` = the source of truth for the
 *     playhead; the UI reads `audioContext.currentTime - startedAtCtxTime
 *     + pausedOffset` on every RAF while playing.
 *   - Solo trumps mute across the mixer: if ANY channel is soloed, only
 *     soloed channels play; muted-but-soloed stays audible (matches every
 *     real DAW). See `channelGain()`.
 *
 * Kept everything inline — one file, no new components — mirrors how the
 * composer is laid out and keeps the whole surface diff'able in one PR.
 */

type StemKey = 'vocals' | 'drums' | 'bass' | 'other';

const STEMS: Array<{ key: StemKey; label: string; icon: string; hue: string; hueDark: string }> = [
  { key: 'vocals', label: 'Vocals', icon: '🎤', hue: '#ec4899', hueDark: '#f472b6' },
  { key: 'drums', label: 'Drums', icon: '🥁', hue: '#f59e0b', hueDark: '#fbbf24' },
  { key: 'bass', label: 'Bass', icon: '🎸', hue: '#8b5cf6', hueDark: '#a78bfa' },
  { key: 'other', label: 'Other', icon: '🎹', hue: '#06b6d4', hueDark: '#22d3ee' },
];

function fmtTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Downsample an AudioBuffer's channel data into a small array of positive
 * peak values suitable for painting a waveform. `targetBins` bins wide —
 * we redraw with the same peaks regardless of canvas width by stretching
 * horizontally, so `targetBins ≈ 240` is plenty for a 640px-wide strip.
 */
function computePeaks(buffer: AudioBuffer, targetBins = 240): number[] {
  const channelData = buffer.getChannelData(0);
  const binSize = Math.max(1, Math.floor(channelData.length / targetBins));
  const peaks: number[] = [];
  for (let bin = 0; bin < targetBins; bin++) {
    let peak = 0;
    const start = bin * binSize;
    const end = Math.min(start + binSize, channelData.length);
    for (let i = start; i < end; i++) {
      const v = Math.abs(channelData[i]);
      if (v > peak) peak = v;
    }
    peaks.push(peak);
  }
  return peaks;
}

/**
 * Paint a waveform onto a canvas. `progress` (0..1) is the playback head;
 * bins before it are drawn in the accent hue, bins after in muted grey.
 * Redrawn on every RAF while playing — cheap enough for 240 bins × 60Hz.
 */
function drawWaveform(
  canvas: HTMLCanvasElement | null,
  peaks: number[] | null,
  progress: number,
  playedColor: string,
  aheadColor: string,
) {
  if (!canvas || !peaks?.length) return;
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const w = Math.round(rect.width * dpr);
  const h = Math.round(rect.height * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  const barWidth = w / peaks.length;
  const mid = h / 2;
  const progressBin = Math.floor(progress * peaks.length);
  for (let i = 0; i < peaks.length; i++) {
    const p = peaks[i];
    const barH = Math.max(2 * dpr, p * (h * 0.92));
    const x = i * barWidth;
    ctx.fillStyle = i <= progressBin ? playedColor : aheadColor;
    // Slim, rounded bars — one draw per bin, cheap.
    ctx.fillRect(x + barWidth * 0.15, mid - barH / 2, barWidth * 0.7, barH);
  }
}

interface DecodedStem {
  key: StemKey;
  buffer: AudioBuffer;
  peaks: number[];
}

export default function StudioPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!user?.email;
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [separateStems, { isLoading: isSubmittingSplit }] = useSeparateStemsMutation();
  // Poll every 3s while the job hasn't produced stems yet. Once ready, keep
  // the polling active (harmless) so a fresh page reload picks up correct
  // state without a race — RTK will cache the response anyway.
  const { data: jobResp, error: jobError, refetch } = useGetJobQuery(jobId!, {
    skip: !jobId,
    pollingInterval: 3000,
  });
  const job = jobResp?.data;
  const stemStatus: 'idle' | 'processing' | 'ready' | 'failed' =
    job?.stems?.status || 'idle';
  const stemUrls = job?.stems?.urls;

  // Web Audio graph — nulled while stems load, populated once decoded.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainNodesRef = useRef<Record<StemKey, GainNode | null>>({
    vocals: null,
    drums: null,
    bass: null,
    other: null,
  });
  const sourceNodesRef = useRef<Record<StemKey, AudioBufferSourceNode | null>>({
    vocals: null,
    drums: null,
    bass: null,
    other: null,
  });
  const [decoded, setDecoded] = useState<Record<StemKey, DecodedStem | null>>({
    vocals: null,
    drums: null,
    bass: null,
    other: null,
  });
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [decoding, setDecoding] = useState(false);

  const [volumes, setVolumes] = useState<Record<StemKey, number>>({
    vocals: 1,
    drums: 1,
    bass: 1,
    other: 1,
  });
  const [muted, setMuted] = useState<Record<StemKey, boolean>>({
    vocals: false,
    drums: false,
    bass: false,
    other: false,
  });
  const [soloed, setSoloed] = useState<Record<StemKey, boolean>>({
    vocals: false,
    drums: false,
    bass: false,
    other: false,
  });

  const [isPlaying, setIsPlaying] = useState(false);
  // playhead in seconds — the tail-authoritative timestamp so pause/resume
  // survives seek. Updated by RAF while playing; set directly on seek.
  const [playhead, setPlayhead] = useState(0);
  // AudioContext time at which the current playback session started. Only
  // valid while `isPlaying`.
  const startedAtCtxTimeRef = useRef(0);
  // Where the session started from (in stem-time seconds) — combined with
  // `currentTime - startedAt` to compute the live playhead.
  const startedFromOffsetRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  // Waveform canvas refs — keyed by stem for scoped access in RAF redraw.
  const canvasRefs = useRef<Record<StemKey, HTMLCanvasElement | null>>({
    vocals: null,
    drums: null,
    bass: null,
    other: null,
  });

  const [bgSurface, bgSurfaceHi, mgLine, mgLine2, mgFg, mgMuted, mgDim, hotAccent, mgBg] = useToken('colors', [
    'mg.surface',
    'mg.surfaceHi',
    'mg.line',
    'mg.line2',
    'mg.fg',
    'mg.muted',
    'mg.dim',
    'hot.500',
    'mg.bg',
  ]);

  const duration = useMemo(() => {
    const bufs = Object.values(decoded).filter(Boolean) as DecodedStem[];
    if (!bufs.length) return 0;
    // Demucs returns identically-shaped stems, but be defensive — a
    // slight mismatch would cut off part of the mix. Take the longest.
    return Math.max(...bufs.map((b) => b.buffer.duration));
  }, [decoded]);

  const allReady = useMemo(
    () => STEMS.every((s) => decoded[s.key] !== null),
    [decoded],
  );

  // Solo trumps mute: if any stem is soloed, only soloed stems play.
  const channelGain = useCallback(
    (key: StemKey): number => {
      const anySoloed = Object.values(soloed).some(Boolean);
      if (anySoloed && !soloed[key]) return 0;
      if (muted[key]) return 0;
      return volumes[key];
    },
    [muted, soloed, volumes],
  );

  // Sync GainNode values whenever the user tweaks a slider / M / S.
  useEffect(() => {
    for (const stem of STEMS) {
      const node = gainNodesRef.current[stem.key];
      if (node) {
        // linearRampToValueAtTime feels more musical than a step change
        // when M/S/volume moves during playback.
        const ctx = audioCtxRef.current;
        const target = channelGain(stem.key);
        if (ctx) {
          node.gain.cancelScheduledValues(ctx.currentTime);
          node.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.03);
        } else {
          node.gain.value = target;
        }
      }
    }
  }, [channelGain]);

  // Fetch + decode stems the moment the job says ready. Idempotent — a
  // second poll won't re-decode because we short-circuit on allReady.
  useEffect(() => {
    if (stemStatus !== 'ready' || !stemUrls) return;
    if (decoding || allReady) return;
    let cancelled = false;
    setDecoding(true);
    setDecodeError(null);

    (async () => {
      try {
        // Reuse the AudioContext across a page's lifetime. Browsers cap
        // per-tab context count; a single one keeps everything cheap.
        const AudioCtor: typeof AudioContext =
          window.AudioContext || (window as any).webkitAudioContext;
        if (!audioCtxRef.current) audioCtxRef.current = new AudioCtor();
        const ctx = audioCtxRef.current;

        const results = await Promise.all(
          STEMS.map(async (stem) => {
            const url = (stemUrls as any)[stem.key] as string | undefined;
            if (!url) throw new Error(`Missing URL for stem "${stem.key}"`);
            const resp = await fetch(url, { credentials: 'omit' });
            if (!resp.ok) throw new Error(`Failed to fetch ${stem.key} (${resp.status})`);
            const arr = await resp.arrayBuffer();
            const buffer = await ctx.decodeAudioData(arr.slice(0)); // slice: some browsers detach
            const peaks = computePeaks(buffer, 240);
            const gain = ctx.createGain();
            gain.gain.value = 1;
            gain.connect(ctx.destination);
            gainNodesRef.current[stem.key] = gain;
            return { key: stem.key, buffer, peaks } as DecodedStem;
          }),
        );

        if (cancelled) return;
        const next = { vocals: null, drums: null, bass: null, other: null } as Record<StemKey, DecodedStem | null>;
        for (const r of results) next[r.key] = r;
        setDecoded(next);
      } catch (err: any) {
        if (cancelled) return;
        setDecodeError(err?.message || 'Failed to load stems');
      } finally {
        if (!cancelled) setDecoding(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stemStatus, stemUrls, allReady, decoding]);

  // Tear down the AudioContext on unmount so leaving Studio doesn't leave
  // a suspended context leaking on the tab.
  useEffect(() => {
    return () => {
      try {
        for (const key of STEMS.map((s) => s.key)) {
          sourceNodesRef.current[key]?.stop();
          sourceNodesRef.current[key]?.disconnect();
          gainNodesRef.current[key]?.disconnect();
        }
        audioCtxRef.current?.close();
      } catch {
        /* ignore — context may already be closed */
      }
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  // Redraw waveforms on every RAF while playing so the "played" region
  // fills smoothly. When paused we redraw once (playhead-triggered).
  useEffect(() => {
    if (!allReady) return;

    const paint = () => {
      const dur = duration || 1;
      const progress = Math.min(1, Math.max(0, playhead / dur));
      for (const stem of STEMS) {
        const color = isDark ? stem.hueDark : stem.hue;
        drawWaveform(
          canvasRefs.current[stem.key],
          decoded[stem.key]?.peaks || null,
          progress,
          color,
          isDark ? 'rgba(237,231,245,0.14)' : 'rgba(30,26,46,0.14)',
        );
      }
    };
    paint();
  }, [playhead, isDark, decoded, allReady, duration]);

  // Playhead RAF loop — only runs while playing.
  useEffect(() => {
    if (!isPlaying) {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      return;
    }
    const step = () => {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const t = ctx.currentTime - startedAtCtxTimeRef.current + startedFromOffsetRef.current;
      if (t >= duration) {
        stopPlayback();
        setPlayhead(0);
        return;
      }
      setPlayhead(t);
      rafIdRef.current = requestAnimationFrame(step);
    };
    rafIdRef.current = requestAnimationFrame(step);
    return () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, duration]);

  const startPlayback = useCallback(
    (fromOffset: number) => {
      const ctx = audioCtxRef.current;
      if (!ctx || !allReady) return;
      if (ctx.state === 'suspended') void ctx.resume();
      // Stop any live sources first — resume-after-seek path.
      for (const stem of STEMS) {
        const prev = sourceNodesRef.current[stem.key];
        if (prev) {
          try { prev.stop(); } catch { /* ignore */ }
          try { prev.disconnect(); } catch { /* ignore */ }
          sourceNodesRef.current[stem.key] = null;
        }
      }
      // Create fresh BufferSourceNodes for this session. All 4 scheduled
      // at exactly the same ctx.currentTime → sample-accurate sync.
      const startAt = ctx.currentTime + 0.05; // small lead-in for scheduling
      for (const stem of STEMS) {
        const d = decoded[stem.key];
        if (!d) continue;
        const src = ctx.createBufferSource();
        src.buffer = d.buffer;
        const gain = gainNodesRef.current[stem.key];
        if (gain) {
          src.connect(gain);
        } else {
          src.connect(ctx.destination);
        }
        src.start(startAt, fromOffset);
        sourceNodesRef.current[stem.key] = src;
      }
      startedAtCtxTimeRef.current = startAt;
      startedFromOffsetRef.current = fromOffset;
      setIsPlaying(true);
    },
    [allReady, decoded],
  );

  const stopPlayback = useCallback(() => {
    for (const stem of STEMS) {
      const prev = sourceNodesRef.current[stem.key];
      if (prev) {
        try { prev.stop(); } catch { /* ignore */ }
        try { prev.disconnect(); } catch { /* ignore */ }
        sourceNodesRef.current[stem.key] = null;
      }
    }
    setIsPlaying(false);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (!allReady) return;
    if (isPlaying) {
      const ctx = audioCtxRef.current;
      if (ctx) {
        const t = ctx.currentTime - startedAtCtxTimeRef.current + startedFromOffsetRef.current;
        setPlayhead(Math.min(duration, Math.max(0, t)));
      }
      stopPlayback();
    } else {
      startPlayback(playhead >= duration ? 0 : playhead);
    }
  }, [allReady, duration, isPlaying, playhead, startPlayback, stopPlayback]);

  const seekTo = useCallback(
    (t: number) => {
      const target = Math.min(duration, Math.max(0, t));
      setPlayhead(target);
      if (isPlaying) startPlayback(target);
    },
    [duration, isPlaying, startPlayback],
  );

  // The "Split into stems" CTA click — kick off the BE job, then rely on
  // the polling GET to progress the UI to processing → ready.
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const handleSplit = useCallback(async () => {
    if (!isAuthed) {
      navigate(`/login?next=/studio/${jobId}`);
      return;
    }
    if (!jobId) return;
    setTriggerError(null);
    try {
      await separateStems({ jobId }).unwrap();
      refetch();
    } catch (err: any) {
      const code = err?.data?.code;
      if (code === 'INSUFFICIENT_CREDITS' || err?.status === 402) {
        setTriggerError('Out of credits — top up in Credits to split more songs.');
      } else if (err?.status === 503) {
        setTriggerError('Stem separation is temporarily unavailable — try again in a moment.');
      } else {
        setTriggerError(err?.data?.message || 'Failed to start stem separation.');
      }
    }
  }, [isAuthed, jobId, navigate, refetch, separateStems]);

  // ---------------- Views ----------------

  if (!jobId) {
    return (
      <Center minH="100vh" bg="mg.bg">
        <Text color="mg.muted">No song selected.</Text>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="mg.bg" color="mg.fg">
      {/* Top bar */}
      <Flex
        align="center"
        justify="space-between"
        px={{ base: 4, md: 6 }}
        py={3}
        borderBottom="1px solid"
        borderColor="mg.line"
        bg="mg.surface"
        position="sticky"
        top={0}
        zIndex={2}
      >
        <IconButton
          aria-label="Back"
          icon={<span aria-hidden style={{ fontSize: 18 }}>←</span>}
          onClick={() => navigate(-1)}
          variant="ghost"
          size="sm"
        />
        <VStack spacing={0} align="center">
          <Text fontSize="xs" color="mg.muted" letterSpacing="wide" textTransform="uppercase">
            Studio
          </Text>
          <Text fontSize="sm" fontWeight={600} noOfLines={1} maxW="60vw">
            {job?.title || 'Untitled song'}
          </Text>
        </VStack>
        <IconButton
          aria-label="Close"
          icon={<span aria-hidden style={{ fontSize: 18 }}>×</span>}
          onClick={() => navigate('/songs')}
          variant="ghost"
          size="sm"
        />
      </Flex>

      {/* Workspace */}
      <Box px={{ base: 3, md: 6 }} py={{ base: 4, md: 8 }} maxW="1000px" mx="auto">
        {jobError ? (
          <StatusCard tone="error">Couldn't load this song.</StatusCard>
        ) : !job ? (
          <StatusCard>
            <Spinner size="sm" mr={3} />
            Loading song…
          </StatusCard>
        ) : stemStatus === 'idle' || stemStatus === 'failed' ? (
          <SplitCta
            onSplit={handleSplit}
            isSubmitting={isSubmittingSplit}
            failedReason={stemStatus === 'failed' ? job?.stems?.errorMessage : null}
            error={triggerError}
          />
        ) : stemStatus === 'processing' ? (
          <ProcessingCard requestedAt={job?.stems?.requestedAt} />
        ) : decodeError ? (
          <StatusCard tone="error">
            <VStack align="start" spacing={2}>
              <Text>Couldn't load the stems.</Text>
              <Text fontSize="xs" color="mg.muted">{decodeError}</Text>
              <Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>
            </VStack>
          </StatusCard>
        ) : !allReady ? (
          <StatusCard>
            <Spinner size="sm" mr={3} />
            Loading tracks into the mixer…
          </StatusCard>
        ) : (
          <>
            {/* Mixer grid */}
            <Box
              display="grid"
              gridTemplateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }}
              gap={{ base: 3, md: 4 }}
              mb={6}
            >
              {STEMS.map((stem) => (
                <ChannelStrip
                  key={stem.key}
                  stem={stem}
                  volume={volumes[stem.key]}
                  onVolume={(v) => setVolumes((s) => ({ ...s, [stem.key]: v }))}
                  muted={muted[stem.key]}
                  onToggleMute={() => setMuted((s) => ({ ...s, [stem.key]: !s[stem.key] }))}
                  soloed={soloed[stem.key]}
                  onToggleSolo={() => setSoloed((s) => ({ ...s, [stem.key]: !s[stem.key] }))}
                  canvasRef={(el) => { canvasRefs.current[stem.key] = el; }}
                  isDark={isDark}
                />
              ))}
            </Box>

            {/* Transport */}
            <Box
              bg="mg.surface"
              borderRadius="xl"
              border="1px solid"
              borderColor="mg.line"
              px={{ base: 4, md: 6 }}
              py={4}
            >
              <HStack spacing={4} align="center">
                <IconButton
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  onClick={togglePlayPause}
                  bg="hot.500"
                  color="white"
                  _hover={{ bg: 'hot.600' }}
                  _active={{ bg: 'hot.700' }}
                  borderRadius="full"
                  size="lg"
                  icon={
                    <span aria-hidden style={{ fontSize: 20, lineHeight: 1 }}>
                      {isPlaying ? '❚❚' : '▶'}
                    </span>
                  }
                />
                <Text fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }} color="mg.fg" minW="42px">
                  {fmtTime(playhead)}
                </Text>
                <Slider
                  aria-label="Playhead"
                  value={duration ? (playhead / duration) * 100 : 0}
                  onChange={(v) => seekTo((v / 100) * duration)}
                  flex="1"
                  min={0}
                  max={100}
                  step={0.1}
                  focusThumbOnChange={false}
                >
                  <SliderTrack bg="mg.line2" h="3px">
                    <SliderFilledTrack bg={hotAccent} />
                  </SliderTrack>
                  <SliderThumb boxSize={4} borderColor="hot.500" />
                </Slider>
                <Text fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }} color="mg.muted" minW="42px" textAlign="right">
                  {fmtTime(duration)}
                </Text>
              </HStack>
              <Text fontSize="xs" color="mg.dim" mt={2} textAlign="center">
                Tap the wave to try things — nothing is saved unless you export. Studio v1 · mixer only
              </Text>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

// ---------------- Sub-components (inline, single-file) ----------------

function StatusCard({
  tone = 'default',
  children,
}: {
  tone?: 'default' | 'error';
  children: React.ReactNode;
}) {
  return (
    <Center
      minH="240px"
      bg="mg.surface"
      borderRadius="xl"
      border="1px solid"
      borderColor={tone === 'error' ? 'red.400' : 'mg.line'}
      px={6}
      py={8}
      color={tone === 'error' ? 'red.500' : 'mg.fg'}
    >
      <Box>{children}</Box>
    </Center>
  );
}

function SplitCta({
  onSplit,
  isSubmitting,
  failedReason,
  error,
}: {
  onSplit: () => void;
  isSubmitting: boolean;
  failedReason: string | null | undefined;
  error: string | null;
}) {
  return (
    <Center
      minH="320px"
      bg="mg.surface"
      borderRadius="2xl"
      border="1px solid"
      borderColor="mg.line"
      px={6}
      py={10}
    >
      <VStack spacing={5} textAlign="center" maxW="420px">
        <Box fontSize="52px" lineHeight={1}>🎛️</Box>
        <VStack spacing={2}>
          <Text fontSize="xl" fontWeight={700}>Open in Studio</Text>
          <Text fontSize="sm" color="mg.muted">
            Split this song into 4 tracks — vocals, drums, bass, and everything else —
            then remix the levels like a mini DAW.
          </Text>
        </VStack>
        <Button
          onClick={onSplit}
          bg="hot.500"
          color="white"
          _hover={{ bg: 'hot.600' }}
          _active={{ bg: 'hot.700' }}
          borderRadius="full"
          px={7}
          size="lg"
          isLoading={isSubmitting}
          loadingText="Starting…"
        >
          Split into stems · 1 credit
        </Button>
        {failedReason && (
          <Text fontSize="xs" color="red.500" mt={1}>
            Last attempt failed: {failedReason}
          </Text>
        )}
        {error && (
          <Text fontSize="xs" color="red.500" mt={1}>
            {error}
          </Text>
        )}
        <Text fontSize="xs" color="mg.dim">
          Takes about 30 seconds. You'll only be charged if the split succeeds.
        </Text>
      </VStack>
    </Center>
  );
}

function ProcessingCard({ requestedAt }: { requestedAt: any }) {
  // Elapsed timer starts from the requestedAt on the doc so a reload
  // during processing shows the true elapsed, not 0:00.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const anchor = requestedAt ? new Date(requestedAt).getTime() : Date.now();
    const tick = () => setElapsed(Math.floor((Date.now() - anchor) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [requestedAt]);
  return (
    <Center
      minH="320px"
      bg="mg.surface"
      borderRadius="2xl"
      border="1px solid"
      borderColor="mg.line"
      px={6}
      py={10}
    >
      <VStack spacing={5} textAlign="center" maxW="380px">
        <Spinner size="xl" thickness="3px" color="hot.500" />
        <VStack spacing={2}>
          <Text fontSize="xl" fontWeight={700}>Splitting your song</Text>
          <Text fontSize="sm" color="mg.muted">
            Isolating vocals, drums, bass, and the rest. This usually takes 20–45 seconds.
          </Text>
          <Text fontSize="xs" color="mg.dim" mt={1}>Elapsed {fmtTime(elapsed)}</Text>
        </VStack>
      </VStack>
    </Center>
  );
}

function ChannelStrip({
  stem,
  volume,
  onVolume,
  muted,
  onToggleMute,
  soloed,
  onToggleSolo,
  canvasRef,
  isDark,
}: {
  stem: { key: StemKey; label: string; icon: string; hue: string; hueDark: string };
  volume: number;
  onVolume: (v: number) => void;
  muted: boolean;
  onToggleMute: () => void;
  soloed: boolean;
  onToggleSolo: () => void;
  canvasRef: (el: HTMLCanvasElement | null) => void;
  isDark: boolean;
}) {
  const accent = isDark ? stem.hueDark : stem.hue;
  return (
    <Box
      bg="mg.surface"
      borderRadius="xl"
      border="1px solid"
      borderColor="mg.line"
      overflow="hidden"
    >
      <HStack
        px={3}
        py={2}
        borderBottom="1px solid"
        borderColor="mg.line"
        bg="mg.surfaceHi"
        spacing={2}
      >
        <Box fontSize="18px" lineHeight={1}>{stem.icon}</Box>
        <Text fontSize="sm" fontWeight={600} flex="1" noOfLines={1}>{stem.label}</Text>
        <Box
          w="8px"
          h="8px"
          borderRadius="full"
          bg={muted ? 'gray.400' : accent}
          opacity={muted ? 0.5 : 1}
          transition="opacity 0.15s, background 0.15s"
        />
      </HStack>

      {/* Waveform — canvas fills the strip; peaks precomputed so RAF is cheap. */}
      <Box px={3} py={3}>
        <Box
          as="canvas"
          ref={canvasRef}
          w="100%"
          h="72px"
          display="block"
          borderRadius="md"
          bg="mg.surfaceLo"
        />
      </Box>

      {/* Volume + M/S */}
      <VStack spacing={3} px={3} pb={3}>
        <Slider
          aria-label={`${stem.label} volume`}
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={onVolume}
          focusThumbOnChange={false}
        >
          <SliderTrack bg="mg.line2" h="3px">
            <SliderFilledTrack bg={accent} />
          </SliderTrack>
          <SliderThumb boxSize={3.5} borderColor={accent} />
        </Slider>
        <HStack spacing={2} w="100%">
          <Button
            size="xs"
            flex="1"
            variant={muted ? 'solid' : 'outline'}
            bg={muted ? 'red.500' : undefined}
            color={muted ? 'white' : 'mg.fg'}
            borderColor="mg.line2"
            _hover={{ bg: muted ? 'red.600' : 'mg.surfaceHi' }}
            onClick={onToggleMute}
            aria-pressed={muted}
          >
            M
          </Button>
          <Button
            size="xs"
            flex="1"
            variant={soloed ? 'solid' : 'outline'}
            bg={soloed ? 'yellow.400' : undefined}
            color={soloed ? 'black' : 'mg.fg'}
            borderColor="mg.line2"
            _hover={{ bg: soloed ? 'yellow.500' : 'mg.surfaceHi' }}
            onClick={onToggleSolo}
            aria-pressed={soloed}
          >
            S
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
